'use client';

import { type StateCreator, create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { env } from '@/lib/env';

interface AccessTokenSlice {
  accessToken: string | null;
  setAccessToken: (accessToken: string | null) => void;
}

interface ReferralCodeSlice {
  referralCode: string | undefined;
  setReferralCode: (referralCode: string | undefined) => void;
}

interface AffilkaIdSlice {
  affilkaId: string | undefined;
  setAffilkaId: (affilkaId: string | undefined) => void;
}

interface SidebarSlice {
  sidebarOpen: boolean;
  activeSidebarSection: 'CASINO' | 'SPORTS' | null;
  toggleSidebar: (state: boolean) => void;
  setActiveSidebarSection: (section: 'CASINO' | 'SPORTS' | null) => void;
}

interface SearchCasinoGamesDialogSlice {
  isSearchCasinoGamesDialogOpen: boolean;
  setIsSearchCasinoGamesDialogOpen: (isSearchCasinoGamesDialogOpen: boolean) => void;
}

interface MoreMenuItemsDialogSlice {
  isMoreMenuItemsDialogOpen: boolean;
  setIsMoreMenuItemsDialogOpen: (isMoreMenuItemsDialogOpen: boolean) => void;
}

interface QuickDepositSlice {
  isQuickDepositOpen: boolean;
  setIsQuickDepositOpen: (isQuickDepositOpen: boolean) => void;
}

interface PathnameHistorySlice {
  currentPathname: string | null;
  prevPathname: string | null;
  setPathnames: (pathname: string) => void;
}

interface UserBalanceSlice {
  balance: number | null;
  hideBalance: boolean;
  toggleBalanceDisplay: () => void;
  setBalance: (balance: number | null) => void;
}

interface UserProfileSlice {
  profileStatus: string | null;
  profileUuid: string | null;
  setProfileStatus: (profile: string | null) => void;
  setProfileUuid: (profileUuid: string | null) => void;
}

interface UserEmailSlice {
  email: string | null;
  setEmail: (email: string | null) => void;
}

interface UserCurrencySlice {
  currency: string | null;
  setCurrency: (currency: string | null) => void;
}

interface UserFavoriteCasinoGamesSlice {
  favoriteCasinoGames: Record<string, string[]>;
  setFavoriteCasinoGame: (gameId: string) => void;
  removeFavoriteCasinoGame: (gameId: string) => void;
}

interface UserGameCloseModalSlice {
  closeGameModal: Record<string, boolean>;
  hideCloseGameModal: () => void;
}

interface UserAirdropRedemptionDialogSlice {
  showAirdropRedemptionDialog: boolean;
  setShowAirdropRedemptionDialog: (showAirdropRedemptionDialog: boolean) => void;
}

export type AppStore = UserBalanceSlice &
  UserProfileSlice &
  UserCurrencySlice &
  UserFavoriteCasinoGamesSlice &
  AccessTokenSlice &
  PathnameHistorySlice &
  UserGameCloseModalSlice &
  UserEmailSlice &
  UserAirdropRedemptionDialogSlice &
  SidebarSlice &
  ReferralCodeSlice &
  AffilkaIdSlice &
  SearchCasinoGamesDialogSlice &
  MoreMenuItemsDialogSlice &
  QuickDepositSlice;

/**
 * The Access Token slice of the store is updated on the Header component
 */
const createAccessTokenSlice: StateCreator<AppStore, [], [], AccessTokenSlice> = (set) => ({
  accessToken: null,
  setAccessToken: (accessToken) => set(() => ({ accessToken })),
});

/**
 * The Balance slice of the store is updated on the HeaderAuthenticatedItems component
 */
const createUserBalanceSlice: StateCreator<AppStore, [], [], UserBalanceSlice> = (set) => ({
  balance: null,
  hideBalance: false,
  toggleBalanceDisplay: () => set((state) => ({ hideBalance: !state.hideBalance })),
  setBalance: (balance) => set(() => ({ balance })),
});

const createUserProfileSlice: StateCreator<AppStore, [], [], UserProfileSlice> = (set) => ({
  profileStatus: null,
  profileUuid: null,
  setProfileStatus: (profileStatus) => set(() => ({ profileStatus })),
  setProfileUuid: (profileUuid) => set(() => ({ profileUuid })),
});

const createUserCurrencySlice: StateCreator<AppStore, [], [], UserCurrencySlice> = (set) => ({
  currency: null,
  setCurrency: (currency) => set(() => ({ currency })),
});

/**
 * State to manage the Player's favorite casino games
 */
const createUserFavoriteCasinoGamesSlice: StateCreator<AppStore, [], [], UserFavoriteCasinoGamesSlice> = (
  set,
  get
) => ({
  favoriteCasinoGames: {},
  setFavoriteCasinoGame: (gameId: string) => {
    const profileUuid = get().profileUuid;

    if (!profileUuid) {
      return;
    }

    set((state) => {
      const currentFavorites = state.favoriteCasinoGames[profileUuid] || [];
      return {
        favoriteCasinoGames: {
          ...state.favoriteCasinoGames,
          [profileUuid]: [...new Set([gameId, ...currentFavorites])],
        },
      };
    });
  },
  removeFavoriteCasinoGame: (gameId: string) => {
    const profileUuid = get().profileUuid;

    if (!profileUuid) {
      return;
    }

    set((state) => {
      const currentFavorites = state.favoriteCasinoGames[profileUuid] || [];
      return {
        favoriteCasinoGames: {
          ...state.favoriteCasinoGames,
          [profileUuid]: currentFavorites.filter((id) => id !== gameId),
        },
      };
    });
  },
});

/**
 * State to manage the game close modal on the game page
 */
const createUserGameCloseModalSlice: StateCreator<AppStore, [], [], UserGameCloseModalSlice> = (set, get) => ({
  closeGameModal: {},
  hideCloseGameModal: () => {
    const profileUuid = get().profileUuid;

    if (!profileUuid) {
      return;
    }

    set((state) => {
      return {
        closeGameModal: {
          ...state.closeGameModal,
          [profileUuid]: !state.closeGameModal[profileUuid],
        },
      };
    });
  },
});

const createPathnameHistorySlice: StateCreator<AppStore, [], [], PathnameHistorySlice> = (set) => ({
  currentPathname: null,
  prevPathname: null,
  setPathnames: (pathname) =>
    set((state) => ({
      prevPathname: state.currentPathname,
      currentPathname: pathname,
    })),
});

/**
 * State to manage the sidebar open/close state and the active sidebar section
 */
const createSidebarSlice: StateCreator<AppStore, [], [], SidebarSlice> = (set) => ({
  sidebarOpen: true,
  activeSidebarSection: null,
  toggleSidebar: (state) => set(() => ({ sidebarOpen: state })),
  setActiveSidebarSection: (state) => set(() => ({ activeSidebarSection: state })),
});

/**
 * State to manage the user's email
 */
const createUserEmailSlice: StateCreator<AppStore, [], [], UserEmailSlice> = (set) => ({
  email: null,
  setEmail: (email) => set(() => ({ email })),
});

const createUserAirdropRedemptionDialogSlice: StateCreator<AppStore, [], [], UserAirdropRedemptionDialogSlice> = (
  set
) => ({
  showAirdropRedemptionDialog: true,
  setShowAirdropRedemptionDialog: (showAirdropRedemptionDialog) => set(() => ({ showAirdropRedemptionDialog })),
});

const createReferralCodeSlice: StateCreator<AppStore, [], [], ReferralCodeSlice> = (set) => ({
  referralCode: undefined,
  setReferralCode: (referralCode) => set(() => ({ referralCode })),
});

const createAffilkaIdSlice: StateCreator<AppStore, [], [], AffilkaIdSlice> = (set) => ({
  affilkaId: undefined,
  setAffilkaId: (affilkaId) => set(() => ({ affilkaId })),
});

/**
 * State to manage the search casino games dialog that opens from the sidebar and the bottom menu
 */
const createSearchCasinoGamesDialogSlice: StateCreator<AppStore, [], [], SearchCasinoGamesDialogSlice> = (set) => ({
  isSearchCasinoGamesDialogOpen: false,
  setIsSearchCasinoGamesDialogOpen: (isSearchCasinoGamesDialogOpen) => set(() => ({ isSearchCasinoGamesDialogOpen })),
});

/**
 * State to manage the more menu items dialog that opens from the bottom menu
 */
const createMoreMenuItemsDialogSlice: StateCreator<AppStore, [], [], MoreMenuItemsDialogSlice> = (set) => ({
  isMoreMenuItemsDialogOpen: false,
  setIsMoreMenuItemsDialogOpen: (isMoreMenuItemsDialogOpen) => set(() => ({ isMoreMenuItemsDialogOpen })),
});

/**
 * State to manage the more menu items dialog that opens from the bottom menu
 */
const createQuickDepositSlice: StateCreator<AppStore, [], [], QuickDepositSlice> = (set) => ({
  isQuickDepositOpen: false,
  setIsQuickDepositOpen: (isQuickDepositOpen) => set(() => ({ isQuickDepositOpen })),
});

/**
 * The App Store is a Zustand store that is used to store the state of the application.
 * It is used to store the state of the application in the browser.
 */
export const createAppStore = (initProps: Partial<AppStore>) => {
  return create<AppStore>()(
    persist(
      (...a) => ({
        ...createAccessTokenSlice(...a),
        ...createUserBalanceSlice(...a),
        ...createUserProfileSlice(...a),
        ...createUserCurrencySlice(...a),
        ...createUserFavoriteCasinoGamesSlice(...a),
        ...createUserGameCloseModalSlice(...a),
        ...createSidebarSlice(...a),
        ...createPathnameHistorySlice(...a),
        ...createUserEmailSlice(...a),
        ...createUserAirdropRedemptionDialogSlice(...a),
        ...createReferralCodeSlice(...a),
        ...createAffilkaIdSlice(...a),
        ...createSearchCasinoGamesDialogSlice(...a),
        ...createMoreMenuItemsDialogSlice(...a),
        ...createQuickDepositSlice(...a),
        ...initProps,
      }),
      {
        name: env.NEXT_PUBLIC_APP_STORE_STORAGE_KEY,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          favoriteCasinoGames: state.favoriteCasinoGames,
          closeGameModal: state.closeGameModal,
          activeSidebarSection: state.activeSidebarSection,
          sidebarOpen: state.sidebarOpen,
          showAirdropRedemptionDialog: state.showAirdropRedemptionDialog,
          referralCode: state.referralCode,
          affilkaId: state.affilkaId,
        }),
      }
    )
  );
};
